<template>
    <div>
		<div v-if="!isServerOnline && !transcoderConfigured && !transmissionCompleted" class="row q-mb-md text-center">
			<div class="col-12">
				<h3 class="text-h3 q-ma-md">{{ this.title }}</h3>
				<p class="text-body1">{{ this.description }}</p>
				<p class="text-weight-medium">{{ getTransmissionDate }}</p>
			</div>
			<div v-if="!isMicrophoneEnabled" class="col-12">
				<h5 class="text-h5">{{ userCantStream }}</h5>
			</div>

			<div v-else class="col-12">
				<q-btn class="q-ma-md" :disabled="isServerOnline" color="primary" :label="$t('configure_streaming')" @click="configureStreamingModal = true" />
			</div>

			<q-modal v-model="configureStreamingModal" :content-css="{minWidth: '60vw', minHeight: '60vh'}">
				<q-modal-layout>
					<q-toolbar slot="header">
						<q-btn flat round dense v-close-overlay icon="close" />
						<q-toolbar-title>{{ $t('configure_streaming') }}</q-toolbar-title>
					</q-toolbar>

					<div class="layout-padding">
						<span class="caption q-mr-md">{{ $t('audio_input_devices') }}</span>
						<q-select v-model="audioInputDevice" :options="audioInputDevices" label="audio_input_devices" />

						<span v-if="hasCamera" class="caption q-mr-md">{{ $t('video_input_devices') }}</span>
						<q-select v-if="hasCamera" v-model="videoInputDevice" :options="videoInputDevices" label="video_input_devices" />

						<span class="caption q-mr-md">{{ $t('streaming_recording_quality') }}</span>
						<q-select v-model="recordingChoice" :options="recordingChoices" label="streaming_recording_quality" />
					</div>

					<q-toolbar slot="footer">
						<q-btn class="q-ma-md" :disabled="isServerOnline" color="positive" :label="$t('save_settings')" @click="startConstraintsAndTranscoder()" />
						<q-btn class="q-ma-md" color="negative" :label="$t('cancel')" @click="configureStreamingModal = false" />
					</q-toolbar>
				</q-modal-layout>
			</q-modal>
		</div>

		<div v-if="transcoderConfigured && !transmissionCompleted || isServerOnline && !transmissionCompleted" class="row q-mb-md">
			<div class="col-12 text-center">
				<q-icon class="q-mx-md" v-if="isLiveStreaming" name="fas fa-broadcast-tower" color="green" />
				<q-btn class="q-ma-md" v-show="!isServerOnline" color="positive" :label="$t('create_transmission')" @click="startWowzaServer()" />
				<q-btn class="q-ma-md" v-show="isServerOnline && localStream && !isLiveStreaming" color="positive" :label="$t('go_live')" @click="startStream()" />
				<q-btn-toggle v-show="localStream" v-model="localVideoFrameWide" toggle-color="positive" :options="localVideoFrameOptions" />
				<q-btn class="q-ma-md" v-show="isServerOnline && localStream && isLiveStreaming" color="negative" :label="$t('end_transmission')" @click="endDialog = true" />
				<!-- DEBUGGER - DON'T DELETE -->
					<!-- <q-btn class="q-ma-md" color="info" :label="$t('CHECK SOURCES')" @click="logSelections" />
					<q-btn class="q-ma-md" :disable="!localStream" color="info" :label="$t('CHECK TRACKS')" @click="checkTracks()" /> -->
				<div class="col-12 q-ma-sm" v-if="transcoderConfigured && !transmissionCompleted && !isServerOnline">
					<p class="text-body1">{{ $t('start_streaming_message') }}</p>
				</div>
			</div>

			<div v-if="!localStream" class="col-12 q-my-md text-center">
				<q-btn size="22px" class="q-ma-md" icon="fas fa-desktop" stack color="primary" :label="$t('start_with_display')" @click="startInitialLocalStream('display')" />
				<q-btn size="22px"  v-if="hasCamera" class="q-ma-md" icon="fas fa-video" stack color="primary" :disable="!hasCamera"  :label="$t('start_with_camera')" @click="startInitialLocalStream('camera')" />
			</div>

			<div v-if="localStream || isLiveStreaming || isServerOnline" class="video-container col-auto q-mx-auto">
				<div class="video-buttons col-12 q-ma-md">
					<q-btn v-if="isCameraActive" class="absolute-left text-white" icon="fas fa-desktop" flat rounded color="primary" :label="$t('share_screen')" @click="setVideoSource('display')" />
					<q-btn v-if="!isCameraActive && isDisplayActive && hasCamera" class="absolute-left text-white" icon="fas fa-video" flat rounded color="primary" :label="$t('share_camera')" @click="setVideoSource('camera')" />
					<q-btn v-if="localStream && isCameraActive" class="absolute-right text-white" icon="fas fa-cog" flat rounded color="primary" :label="$t('device_options')" @click="settingsModal = true"/>
				</div>

				<video id="localVideo" ref="vid" :class="!localVideoFrameWide ? 'frame-narrow' : 'frame-wide'" :srcObject.prop="localStream" autoplay playsinline muted>
				</video>
			</div>
		</div>

		<div v-else class="col-12 q-ma-md text-center">
			<div v-if="transmissionCompleted && !transmissionDownloaded && !isConvertingVideo">
				<p class="text-body1 text-center q-ma-md">{{ transmissionFinished }}</p>
				<q-btn class="q-ma-md" color="primary" :label="$t('save_webinar_video')" @click="saveStream()" />
				<p class="text-body2 text-weight-medium q-ma-md">{{ $t('record_warning') }}</p>
			</div>
			<div v-if="transmissionCompleted && !transmissionDownloaded && isConvertingVideo">
				<p class="text-body1 text-center q-ma-md">{{ transmissionFinished }}</p>
			</div>
			<div v-if="transmissionCompleted && transmissionDownloaded">
				<p class="text-body1 text-center q-ma-md">{{ transmissionFinished }}</p>
			</div>
		</div>


		<q-modal v-model="settingsModal" :content-css="{minWidth: '60vw', minHeight: '60vh'}">
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense v-close-overlay icon="close" />
					<q-toolbar-title>{{$t('Settings')}}</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<span class="caption q-mr-md">{{ $t('audio_input_devices') }}</span>
					<q-select v-model="audioInputDevice" :options="audioInputDevices" label="audio_input_devices" />

					<span v-if="hasCamera" class="caption q-mr-md">{{ $t('video_input_devices') }}</span>
					<q-select v-model="videoInputDevice" :options="videoInputDevices" label="video_input_devices" />
				</div>

				<q-toolbar slot="footer">
					<q-btn class="q-mx-xs" color="positive" :label="$t('Ok')" @click="updateDeviceSettings" />
				</q-toolbar>

			</q-modal-layout>
		</q-modal>

		<q-dialog v-model="endDialog">
				<span slot="title">{{ $t('end_transmission') }}</span>
				<span slot="message">{{ $t('confirm_end_transmission') }}</span>

				<template slot="buttons">
						<q-btn color="secondary" :label="$t('cancel')" @click="endDialog = false" />
						<q-btn color="negative" :label="$t('end_transmission')" @click="stopStreaming()" />
				</template>
		</q-dialog>

    </div>
</template>

<script>
import adapter from 'webrtc-adapter'
import { mapGetters } from 'vuex'
import { QSpinnerGears, date } from 'quasar'
const { formatDate } = date

export default {
	props: ['id_webcast'],

	data() {
		return {
			hasCamera: null,
			hasMicrophone: null,
			isMicrophoneEnabled: false,
			configureStreamingModal: false,
			recordingChoice: 2,
			recordingChoices: [
				{ value: 1, width: 854, height: 480, profile: "main", bitRate: 3500, label: 'FWVGA (480p)'},
				{ value: 2, width: 1280, height: 720, profile: "high", bitRate: 5000, label: 'HD (720p)'},
				{ value: 3, width: 1920, height: 1080, profile: "high", bitRate: 6500, label: 'FullHD (1080p)'}
			],
			audioInputDevice: null,
            audioInputDevices: [],
			// Audio codecs: opus (default),isac, g722, pcmu, pcma, cn
			// https://developer.mozilla.org/en-US/docs/Web/Media/Formats/Audio_codecs
            audioSettings: {
                audioBitrate: 256,
                audioChoice: 'opus',
                audioIndex: -1
            },
            audioStream: null,
            audioTrack: null,
            constraintsCamera: {
                audio: true,
                video: {
                    height: {
                        ideal: 720,
                        max: 1080,
                        min: 360
                    },
                    width: {
                        ideal: 1280,
                        max: 1920,
                        min: 640
                    }
                }
            },
            constraintsDisplay: {
                audio: true,
                video: true
            },
            description: '',
            elementText: {
                sdpDataTag: null
            },
            endDialog: false,
            isBrowserCapable: false,
            isCameraActive: false,
            isDisplayActive: false,
            isLiveStreaming: false,
            isLocalVideoPlaying: false,
            isServerOnline: false,
            localStream: null,
            localVideoFrameOptions: [{
                icon: 'fas fa-compress-arrows-alt',
                value: false
            }, {
                icon: 'fas fa-expand-arrows-alt',
                value: true
            }],
            localVideoFrameWide: false,
            newAPI: false,
            peerConnection: null,
            peerConnectionConfig: {
                iceServers: []
            },
            SDPOutput: new Object(),
			settingsModal: false,
			constraintsFallback: {
				audio: true,
				video: true
			},
            constraintsStart: {
                audio: null,
                video: null
            },
            streamCheckInterval: null,
            streamInfo: {
                applicationName: '',
                sessionId: '[empty]',
                streamName: ''
            },
            title: '',
			transmission_date: '',
			isConvertingVideo: false,
			transmissionCompleted: false,
			transmissionDownloaded: false,
            userAgent: null,
            userData: {
                param1: 'value1'
            },
            videoInputDevice: null,
            videoInputDevices: [],
            videoSettings: {
                videoBitrate: 6500,
                videoChoice: '42e01f',
                videoFrameRate: '29.97',
                videoIndex: -1
            },
            videoTrack: null,
            wsConnection: null,
			wsURL: '',
			transcoderConfigured: false,
			transcoderSettings: {
				"aspect_ratio_width": 1280,
        		"aspect_ratio_height": 720,
				"bitrate_video": 5600,
				"h264_profile": "high"
			},
			vueRTCPC: window.RTCPeerConnection || window.mozRTCPeerConnection || window.webkitRTCPeerConnection,
			vueRTCIC: window.RTCIceCandidate || window.mozRTCIceCandidate || window.webkitRTCIceCandidate,
			vueRTCSD: window.RTCSessionDescription || window.mozRTCSessionDescription || window.webkitRTCSessionDescription
        }
	},

	components: { QSpinnerGears },

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			clients: 'params/clients',
			id_user_client_selected: 'common/id_user_client_selected',
			faq_categories: 'registration_lists/faq_categories',
			areas: 'registration_lists/country_areas',
			user_groups: 'registration_lists/group_users',
			profiles: 'registration_lists/profiles',
			scores: 'registration_lists/group_training_scores',
			tags: 'registration_lists/tags',
			login_oauth2: 'params/login_oauth2',
			user_module_items: 'common/user_module_items',
		}),

		getHeader() {
			let headers = {}
			return headers = {
				headers: {
				'content-type': 'application/json',
				'authorization': 'Basic ' + btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
				'authorization': 'Bearer ' + this.access_token
				}
			}
		},

		getTransmissionDate() {
			return `${this.$t('webinar_start_schedule')}: ${date.formatDate(this.transmission_date, 'YYYY-MM-DD HH:mm')}`
		},

		userCantStream() {
				return `${this.$t('you_need_at_least_a_microphone_to_stream')}`
		},

		transmissionFinished() {
			if (this.transmissionCompleted && !this.transmissionDownloaded && !this.isConvertingVideo) {
				return this.$t('transmission_ended_ready_to_download')
			} else if (this.transmissionCompleted && !this.transmissionDownloaded && this.isConvertingVideo) {
				return this.$t('transmission_ended_saving_video')
			} else {
				return this.$t('transmission_ended_ready_to_watch')
			}
		},

	},

	methods: {
		// TEST
		/* logSelections() {
			console.log('Startup const: ', this.constraintsStart)
			console.log('VideoInput: ', this.videoInputDevice)
			console.log('AudioInput: ', this.audioInputDevice)
			console.log('Transcoder: ', this.transcoderSettings)
		}, */

		fetchData() {
			this.$q.loading.show()

			this.$axios.get(`/admin_webcasts/webcasts/${this.id_webcast}/`, this.getHeader)
			.then(response => {
				this.title = response.data.title
				this.description = response.data.description
				this.transmission_date = response.data.transmission_date
				this.wsURL = response.data.webcast_wowza_sdp.sdp_url
				this.streamInfo.applicationName = response.data.webcast_wowza_sdp.application_name
				this.streamInfo.streamName = response.data.webcast_wowza_sdp.stream_name
				this.transmissionCompleted = response.data.transmission_completed
				this.transmissionDownloaded = response.data.transmission_downloaded

				if (response.data.status === 5) {
					this.isConvertingVideo = true
				}
			})
			.catch(e => {
				console.log(e)
			})
			.finally(() => {
				this.$q.loading.hide()
			})
		},

		// Ask back-end to check for Streaming conversion. If it has finished, send the streaming video to S3
		saveStream() {
			this.$q.loading.show()

			this.$axios.get(`/admin_webcasts/webcasts/${this.id_webcast}/download_streaming/`, this.getHeader)
				.then(response => {
					if(response.status === 202) {
						this.$q.notify({
								message: this.$t('transmission_download_ok'),
								type: 'positive',
								icon: 'far fa-save'
							})
						this.transmissionDownloaded = true
						this.$q.loading.hide()
					}
				})
				.catch(e => {
					this.$q.loading.hide()
					this.$q.notify({
							message: this.$t('transmission_download_error'),
							type: 'warning',
							icon: 'fas fa-exclamation-triangle'
						})
					this.transmissionDownloaded = false
					console.log(e)
				})
		},

		setVideoSource(source) {
			if(source === 'display') this.changeCapture('display')
			if(source === 'camera') this.changeCapture('camera')
		},

		// Get the media devices like cameras or the display monitor
		changeCapture(source) {
			// Using screen display: https://webrtc.org/getting-started/media-capture-and-constraints
			if (source === 'display') {
				const openMediaDevices = async constraints => {
					if (this.localStream) {
						const currentVideoTrack = this.localStream.getVideoTracks()
							currentVideoTrack.forEach((track) => {
								track.stop()
						})
					}
					this.localStream = await navigator.mediaDevices.getDisplayMedia(constraints)
					this.isCameraActive = false
					this.isDisplayActive = true
					this.isLocalVideoPlaying = true
					const localStreamTracks = await this.localStream.getTracks()

					if (localStreamTracks.length < 2) {
						this.localStream.addTrack(this.audioTrack, this.localStream)
					}

					await this.updateLiveStream()
					return this.localStream
				}
				try {
					// console.log('Open Media Devices: ')
					this.localStream = openMediaDevices(this.constraintsDisplay)
					// console.log('Got MediaStream:', stream)

				} catch (error) {
					this.isCameraActive = true
					this.isDisplayActive = false
					console.error('Error accessing media devices.', error)
				}

			} else {

				const openMediaDevices = async constraints => {
					if (this.localStream) {
						const currentVideoTrack = this.localStream.getVideoTracks()
							currentVideoTrack.forEach((track) => {
								track.stop()
						})
					}
					this.localStream = await navigator.mediaDevices.getUserMedia(constraints)
					this.isCameraActive = true
					this.isDisplayActive = false
					this.isLocalVideoPlaying = true
					await this.updateLiveStream()
					return this.localStream
				}
				try {
					if (!this.transcoderConfigured) {
						this.constraintsCamera = this.constraintsFallback
					}
					this.localStream = openMediaDevices(this.constraintsCamera)
					// console.log('Got MediaStream:', stream)

				} catch (error) {
					this.isCameraActive = false
					this.isDisplayActive = true
					console.error('Error accessing media devices.', error)
				}
			}
		},

		// Update the live stream, when changing cam <=> monitor (server update)
		async updateLiveStream() {

			if (this.peerConnection) {
				let pc = this.peerConnection
				let senders = pc.getSenders()
				// console.log('The senders')
				// console.log(senders)
				let videoTrack = this.localStream.getVideoTracks()[0]
				let sender = pc.getSenders().find((source) => {
					return source.track.kind == videoTrack.kind
				})
				sender.replaceTrack(videoTrack)
			}
			return
		},

		// TEST FUNCTION TO CHECK THE VIDEO TRACKS
		// DON'T DELETE
		/* checkTracks() {
			let tracks = this.localStream.getTracks()
				console.log('LocalTracks: ')
				console.log(tracks)
			if (this.peerConnection) {
				let senders = this.peerConnection.getSenders()
				console.log('RTCRtpSenders: ')
				console.table(senders)
				console.log('Sender 1: ', senders[0])
				console.log('Sender 2: ', senders[1])
			}
		}, */

		// Stop streaming, then stop the publisher (Wowza) delay added to avoid video cut in the end
		stopStreaming() {
			const spinner = typeof QSpinnerGears !== 'undefined'
				? QSpinnerGears
				: Quasar.components.QSpinnerGears

			this.$q.loading.show({
				spinner,
				spinnerColor: 'white',
				messageColor: 'white',
				message: this.$t('ending_transmission')
			})
			this.endDialog = false

			setTimeout(() => {
				this.audioTrack.stop()
				this.videoTrack.stop()

				if (!this.localStream) {
					this.localStream = null
					this.isServerOnline = false
				} else {
					const tracks = this.localStream.getTracks()
					tracks.forEach((track) => {
						track.stop()
					})
				}
					this.$refs.vid.srcObject = null
					this.audioStream = null
					this.localStream = null
					this.audioTrack = null
					this.videoTrack = null
					this.videoInputDevice = null
					this.audioInputDevice = null
					this.isCameraActive = false
					this.isDisplayActive = false
					this.isLocalVideoPlaying = false
					this.isLiveStreaming = false
			}, 7000)

			setTimeout(() => {
				this.stopPublisher()

				this.$axios.get(`/admin_webcasts/webcasts/${this.id_webcast}/stop_streaming/`, this.getHeader)
					.then(() => {
						this.$q.loading.hide()
					})
					.catch(e => {
						this.$q.loading.hide()
						console.log(e)
					})
					.finally(() => {
						this.$q.notify({
							message: this.$t('transmission_ended'),
							icon: 'fas fa-broadcast-tower',
							color: 'info'
						})
						this.isServerOnline = false
						this.transmissionCompleted = true
					})
			}, 7100)
		},

		// Will send the streaming data to Wowza
		// Also, check the online streaming state here
		wsConnect(url) {
			this.wsConnection = new WebSocket(this.wsURL)
			this.wsConnection.binaryType = 'arraybuffer'

			this.wsConnection.onopen = () => {
				// console.log('wsConnection.onopen')

				this.peerConnection = new RTCPeerConnection(this.peerConnectionConfig)
				this.peerConnection.onicecandidate = this.gotIceCandidate

				if (this.newAPI) {
					let localTracks = this.localStream.getTracks()
					for (let localTrack of localTracks) {
						this.peerConnection.addTrack(localTrack, this.localStream)
					}
					// console.log(localTracks)
				} else {
					this.peerConnection.addStream(this.localStream)
				}

				this.peerConnection
				.createOffer()
				.then(this.gotDescription)
				.catch(this.errorHandler)
			}

			// var offerOptions = {
			// New spec states offerToReceiveAudio/Video are of type long (due to
			// having to tell how many "m" lines to generate).
			// http://w3c.github.io/webrtc-pc/#idl-def-RTCOfferAnswerOptions.
			//  offerToReceiveAudio: 1,
			// offerToReceiveVideo: 1,
			//	codecPayloadType: 0x42E01F,
			// }

			this.wsConnection.onmessage = evt => {
				// console.log('wsConnection.onmessage: ', evt.data)

				let msgJSON = JSON.parse(evt.data)

				let msgStatus = Number(msgJSON['status'])
				let msgCommand = msgJSON['command']

				if (msgStatus != 200) {
					this.elementText.sdpDataTag = msgJSON['statusDescription']
					this.stopPublisher()
				} else {
					this.elementText.sdpDataTag = ''

					let sdpData = msgJSON['sdp']
					if (sdpData !== undefined) {
						// console.log('sdp: ', msgJSON['sdp'])

						this.peerConnection
						.setRemoteDescription(new RTCSessionDescription(sdpData))
						.catch(this.errorHandler)
					}

					let iceCandidates = msgJSON['iceCandidates']
					if (iceCandidates !== undefined) {
						// State of the online streaming
						this.isLiveStreaming = true
						for (let iceCandidate of iceCandidates) {
							// console.log('iceCandidates: ', iceCandidate)

							this.peerConnection.addIceCandidate(
							new RTCIceCandidate(iceCandidate)
							)
						}
					}
				}

				if (this.wsConnection != null) this.wsConnection.close()
				this.wsConnection = null
			}

			this.wsConnection.onclose = () => {
				console.log('wsConnection.onclose')
			}

			this.wsConnection.onerror = evt => {
				console.log('wsConnection.onerror: ' + JSON.stringify(evt))

				this.elementText.sdpDataTag =
				'WebSocket connection failed: ' + this.wsURL
				this.stopPublisher()
			}
		},

		startPublisher() {
			this.wsURL
			this.streamInfo.applicationName
			this.streamInfo.streamName
			this.audioSettings.audioBitrate
			this.audioSettings.audioChoice
			this.videoSettings.videoBitrate
			this.videoSettings.videoChoice
			this.videoSettings.videoFrameRate
			this.userAgent

			// console.log('startPublisher: wsURL:', this.wsURL)
			// console.log(' streamInfo:', JSON.stringify(this.streamInfo))

			this.wsConnect(this.wsURL)
		},

		stopPublisher() {
			if (this.peerConnection != null) this.peerConnection.close()
			this.peerConnection = null

			if (this.wsConnection != null) this.wsConnection.close()
			this.wsConnection = null
		},

		checkWowzaStreamState() {
			this.streamCheckInterval = setInterval(this.streamState, 7500)
		},

		// Check the stream state (Wowza server information)
		streamState() {
			this.$axios.get(`/admin_webcasts/webcasts/${this.id_webcast}/streaming_state/`, this.getHeader)
				.then(response => {
					// console.log('Stream status:')
					if (response.data.live_stream.state === 'stopped') {
						this.isServerOnline = false
					}	else if  (response.data.live_stream.state === 'starting') {
						this.isServerOnline = true
					}	else {
							this.$q.loading.hide()
							this.serverStarted()
					}
				})
				.catch(e => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		startWowzaServer() {
			const spinner = typeof QSpinnerGears !== 'undefined'
				? QSpinnerGears
				: Quasar.components.QSpinnerGears

			this.$q.loading.show({
				spinner,
				spinnerColor: 'white',
				messageColor: 'white',
				message: this.$t('please_wait_streaming_server_starting')
			})

			this.$axios.get(`/admin_webcasts/webcasts/${this.id_webcast}/start_streaming/`, this.getHeader)
				.then(response => {
					if (response.data.meta) {
						this.isServerOnline = true
						this.$q.loading.hide()
					} else if (response.data) {
						if (response.data.live_stream.state === 'starting') this.checkWowzaStreamState()
					}
				})
				.catch(e => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		serverStarted() {
			clearInterval(this.streamCheckInterval)
			this.$q.notify({
				message: this.$t('transmission_started_ready_to_go_live'),
				icon: 'fas fa-broadcast-tower',
				color: 'positive'
			})
			this.isServerOnline = true
		},

		startStream() {
			if (this.localStream === null) {
				this.$q.notify({
					message: this.$t('select_capture_device_first'),
					icon: 'fas fa-exclamation-triangle',
					color: 'warning'
				})
			} else {
				if (this.peerConnection == null && this.isServerOnline) this.startPublisher()
				else this.stopPublisher()
			}
		},

		// Ice Candidate WebRTC Server comms
		gotIceCandidate(event) {
			if (event.candidate != null) {
				// console.log('gotIceCandidate: ' + JSON.stringify({ ice: event.candidate }))
			}
		},

		// WebRTC Description
		gotDescription(description) {
			let enhanceData = new Object()

			if (this.audioSettings.audioBitrate !== undefined)
				enhanceData.audioBitrate = this.audioSettings.audioBitrate
			if (this.videoSettings.videoBitrate !== undefined)
				enhanceData.videoBitrate = this.videoSettings.videoBitrate
			if (this.videoSettings.videoFrameRate !== undefined)
				enhanceData.videoFrameRate = this.videoSettings.videoFrameRate

			description.sdp = this.enhanceSDP(description.sdp, enhanceData)

			// console.log('gotDescription: ', JSON.stringify({ sdp: description }))

			return this.peerConnection
				.setLocalDescription(description)
				.then(() => {
					this.wsConnection.send(
						'{"direction":"publish", "command":"sendOffer", "streamInfo":' +
						JSON.stringify(this.streamInfo) +
						', "sdp":' +
						JSON.stringify(description) +
						', "userData":' +
						JSON.stringify(this.userData) +
						'}'
					)

					// Tell the back end that you started streaming: Is live
					setTimeout(() => {
						this.isLiveStreaming = true
						this.$axios.get(`/admin_webcasts/webcasts/${this.id_webcast}/streaming_is_live/`, this.getHeader)
						.then(response => {
							})
							.catch(e => {
								console.log(e)
							})

						this.$q.notify({
							message: this.$t('live'),
							icon: 'fas fa-broadcast-tower',
							color: 'positive'
						})
					}, 2500)

				})
				.catch(err => {
					console.log('Set local description error:')
					console.log(err)
					this.isLiveStreaming = false
				})
		},

		// Add audio to peerConnection
		addAudio(sdpStr, audioLine) {
			let sdpLines = sdpStr.split(/\r\n/)
			let sdpSection = 'header'
			let hitMID = false
			let sdpStrRet = ''
			let done = false

			for (let sdpLine of sdpLines) {
				if (sdpLine.length <= 0) continue

				sdpStrRet += sdpLine
				sdpStrRet += '\r\n'

				if ('a=rtcp-mux'.localeCompare(sdpLine) == 0 && done == false) {
					sdpStrRet += audioLine
					done = true
				}
			}
			// console.log('SdpStrRet: ')
			// console.log(sdpStrRet)
			return sdpStrRet
		},

		// Add video to peerConnection
		addVideo(sdpStr, videoLine) {
			let sdpLines = sdpStr.split(/\r\n/)
			let sdpSection = 'header'
			let hitMID = false
			let sdpStrRet = ''
			let done = false

			let rtcpSize = false
			let rtcpMux = false

			for (let sdpLine of sdpLines) {
				if (sdpLine.length <= 0) continue

				if (sdpLine.includes('a=rtcp-rsize')) {
					rtcpSize = true
				}

				if (sdpLine.includes('a=rtcp-mux')) {
					rtcpMux = true
				}
			}

			for (let sdpLine of sdpLines) {
				sdpStrRet += sdpLine
				sdpStrRet += '\r\n'

				if ('a=rtcp-rsize'.localeCompare(sdpLine) == 0 && done == false && rtcpSize == true) {
					sdpStrRet += videoLine
					done = true
				}

				if ('a=rtcp-mux'.localeCompare(sdpLine) == 0 && done == true && rtcpSize == false) {
					sdpStrRet += videoLine
					done = true
				}

				if ('a=rtcp-mux'.localeCompare(sdpLine) == 0 && done == false && rtcpSize == false) {
					done = true
				}
			}
			return sdpStrRet
		},

		enhanceSDP(sdpStr, enhanceData) {
			let sdpLines = sdpStr.split(/\r\n/)
			let sdpSection = 'header'
			let hitMID = false
			let sdpStrRet = ''

			// console.log("Original SDP: ", sdpStr)

			// Firefox provides a reasonable SDP, Chrome is just odd
			// so we have to doing a little mundging to make it all work
			if (
				!sdpStr.includes('THIS_IS_SDPARTA') ||
				this.videoSettings.videoChoice.includes('VP9')
			) {
				for (let sdpLine of sdpLines) {
				if (sdpLine.length <= 0) continue

				let doneCheck = this.checkLine(sdpLine)
				if (!doneCheck) continue

				sdpStrRet += sdpLine
				sdpStrRet += '\r\n'
				}
				sdpStrRet = this.addAudio(
				sdpStrRet,
				this.deliverCheckLine(this.audioSettings.audioChoice, 'audio')
				)
				sdpStrRet = this.addVideo(
				sdpStrRet,
				this.deliverCheckLine(this.videoSettings.videoChoice, 'video')
				)
				sdpStr = sdpStrRet
				sdpLines = sdpStr.split(/\r\n/)
				sdpStrRet = ''
			}

			for (let sdpLine of sdpLines) {
				if (sdpLine.length <= 0) continue

				if (sdpLine.indexOf('m=audio') == 0 && this.audioSettings.audioIndex != -1) {
					let audioMLines = sdpLine.split(' ')
					sdpStrRet +=
						audioMLines[0] +
						' ' +
						audioMLines[1] +
						' ' +
						audioMLines[2] +
						' ' +
						this.audioSettings.audioIndex
				} else if (sdpLine.indexOf('m=video') == 0 && this.videoSettings.videoIndex != -1) {
					let audioMLines = sdpLine.split(' ')
					sdpStrRet +=
						audioMLines[0] +
						' ' +
						audioMLines[1] +
						' ' +
						audioMLines[2] +
						' ' +
						this.videoSettings.videoIndex
				} else {
					sdpStrRet += sdpLine
				}

				if (sdpLine.indexOf('m=audio') === 0) {
					sdpSection = 'audio'
					hitMID = false
				} else if (sdpLine.indexOf('m=video') === 0) {
					sdpSection = 'video'
					hitMID = false
				} else if (sdpLine.indexOf('a=rtpmap') == 0) {
					sdpSection = 'bandwidth'
					hitMID = false
				}

				if (sdpLine.indexOf('a=mid:') === 0 || sdpLine.indexOf('a=rtpmap') == 0) {
					if (!hitMID) {
						if ('audio'.localeCompare(sdpSection) == 0) {
							if (enhanceData.audioBitrate !== undefined) {
								sdpStrRet += '\r\nb=CT:' + enhanceData.audioBitrate
								sdpStrRet += '\r\nb=AS:' + enhanceData.audioBitrate
							}
							hitMID = true
						} else if ('video'.localeCompare(sdpSection) == 0) {
							if (enhanceData.videoBitrate !== undefined) {
								sdpStrRet += '\r\nb=CT:' + enhanceData.videoBitrate
								sdpStrRet += '\r\nb=AS:' + enhanceData.videoBitrate
									if (enhanceData.videoFrameRate !== undefined) {
										sdpStrRet += '\r\na=framerate:' + enhanceData.videoFrameRate
									}
							}
							hitMID = true
						} else if ('bandwidth'.localeCompare(sdpSection) == 0) {
							let rtpmapID
							rtpmapID = this.getrtpMapID(sdpLine)
							if (rtpmapID !== null) {
									let match = rtpmapID[2].toLowerCase()
								if (
								'vp9'.localeCompare(match) == 0 ||
								'vp8'.localeCompare(match) == 0 ||
								'h264'.localeCompare(match) == 0 ||
								'red'.localeCompare(match) == 0 ||
								'ulpfec'.localeCompare(match) == 0 ||
								'rtx'.localeCompare(match) == 0
								) {
									if (enhanceData.videoBitrate !== undefined) {
										sdpStrRet +=
										'\r\na=fmtp:' +
										rtpmapID[1] +
										' x-google-min-bitrate=' +
										enhanceData.videoBitrate +
										';x-google-max-bitrate=' +
										enhanceData.videoBitrate
									}
								}

								if (
								'opus'.localeCompare(match) == 0 ||
								'isac'.localeCompare(match) == 0 ||
								'g722'.localeCompare(match) == 0 ||
								'pcmu'.localeCompare(match) == 0 ||
								'pcma'.localeCompare(match) == 0 ||
								'cn'.localeCompare(match) == 0
								) {
									if (enhanceData.audioBitrate !== undefined) {
										sdpStrRet +=
										'\r\na=fmtp:' +
										rtpmapID[1] +
										' x-google-min-bitrate=' +
										enhanceData.audioBitrate +
										';x-google-max-bitrate=' +
										enhanceData.audioBitrate
									}
								}
							}
						}
					}
				}
				sdpStrRet += '\r\n'
			}
			// console.log('Resulting SDP: ', sdpStrRet)
			return sdpStrRet
		},

		// Checks the deliver SDP output
		deliverCheckLine(profile, type) {
			let outputString = ''
			for (let line in this.SDPOutput) {
				let lineInUse = this.SDPOutput[line]
				outputString += line
				if (lineInUse.includes(profile)) {
					if (profile.includes('VP9') || profile.includes('VP8')) {
						let output = ''
						let outputs = lineInUse.split(/\r\n/)
						for (let position in outputs) {
							let transport = outputs[position]
							if (
								transport.indexOf('transport-cc') !== -1 ||
								transport.indexOf('goog-remb') !== -1 ||
								transport.indexOf('nack') !== -1
							) {
								continue
							}
							output += transport
							output += '\r\n'
						}

						if (type.includes('audio')) {
							this.audioSettings.audioIndex = line
						}

						if (type.includes('video')) {
							this.videoSettings.videoIndex = line
						}

						return output
					}

					if (type.includes('audio')) {
						this.audioSettings.audioIndex = line
					}

					if (type.includes('video')) {
						this.videoSettings.videoIndex = line
					}
					return lineInUse
				}
			}
			return outputString
		},

		// Perform for the protocol Real time transport, etc..
		// map from an RTP payload type number to a media encoding name that identifies the payload format
		checkLine(line) {
			if (
				line.startsWith('a=rtpmap') ||
				line.startsWith('a=rtcp-fb') ||
				line.startsWith('a=fmtp')
			) {
				let res = line.split(':')

				if (res.length > 1) {
					let number = res[1].split(' ')
					if (!isNaN(number[0])) {
						if (!number[1].startsWith('http') && !number[1].startsWith('ur')) {
							let currentString = this.SDPOutput[number[0]]
							if (!currentString) {
								currentString = ''
							}
						currentString += line + '\r\n'
						this.SDPOutput[number[0]] = currentString
						return false
						}
					}
				}
			}
			return true
		},

		// Checks for the rtpmapID in the SDP protocol (i.e.: a=rtpmap:0 PCMU/8000)
		getrtpMapID(line) {
			let findid = new RegExp('a=rtpmap:(\\d+) (\\w+)/(\\d+)')
			let found = line.match(findid)
			return found && found.length >= 3 ? found : null
		},

		errorHandler(error) {
			console.log(error)
		},

		// Change device settings for microphones and/or cams
		async updateDeviceSettings() {
			this.settingsModal = false

			this.constraintsCamera = {
				audio: { deviceId: this.audioInputDevice ? { exact: this.audioInputDevice } : undefined },
				video: { deviceId: this.videoInputDevice ? { exact: this.videoInputDevice } : undefined }
			}

			await navigator.mediaDevices.getUserMedia(this.constraintsCamera)
			.then(() => {
				const tracks = this.localStream.getTracks()
				tracks.forEach(track => {
						track.applyConstraints(this.constraintsCamera)
					})
			})
			.catch(err => console.log(err))
		},

		// Start displaying the local video - camera or display
		startInitialLocalStream(source) {
			if (this.constraintsStart.audio === null) {
				this.constraintsStart = this.constraintsFallback
			}
			// Using screen display: https://webrtc.org/getting-started/media-capture-and-constraints
			if (source === 'display') {
				const openMediaDevices = async constraints => {
					if (this.localStream) {
						const currentVideoTrack = this.localStream.getVideoTracks()
							currentVideoTrack.forEach((track) => {
								track.stop()
						})
					}
					this.localStream = await navigator.mediaDevices.getDisplayMedia(constraints)
					const localStreamTracks = await this.localStream.getTracks()

					if (this.localStream) {
						this.isCameraActive = false
						this.isDisplayActive = true
						this.isLocalVideoPlaying = true
						if (localStreamTracks.length < 2) {
							this.localStream.addTrack(this.audioTrack, this.localStream)
						}
					}

					this.videoTrack = await this.localStream.getVideoTracks()[0]
					return this.localStream
				}
				try {
					// console.log('Open Media Devices: ')
					this.audioTrack = this.audioStream.getAudioTracks()[0]

					if (this.transcoderConfigured) {
						this.localStream = openMediaDevices({
							audio: true,
							video: {
								width: this.transcoderSettings.aspect_ratio_width,
								height: this.transcoderSettings.aspect_ratio_height
							}
						})
					} else {
						this.localStream = openMediaDevices(this.constraintsFallback)
					}
					// console.log('Got MediaStream:', stream)

				} catch (error) {
					this.localStream = null
					this.isLocalVideoPlaying = false
					console.error('Error accessing media devices.', error)

				} finally {
					if (!this.videoTrack) {
						this.localStream = null
						this.isLocalVideoPlaying = false
						this.isCameraActive = false
						this.isDisplayActive = false
						this.isLocalVideoPlaying = false
					}
				}

			} else {

				const openMediaDevices = async constraints => {
					if (this.localStream) {
						const currentVideoTrack = this.localStream.getVideoTracks()
							currentVideoTrack.forEach((track) => {
								track.stop()
						})
					}
					this.localStream = await navigator.mediaDevices.getUserMedia(constraints)
					this.videoTrack = await this.localStream.getVideoTracks()[0]

					if (this.localStream) {
						this.isCameraActive = true
						this.isDisplayActive = false
						this.isLocalVideoPlaying = true
					}

					return this.localStream
				}
				try {
					this.audioTrack = this.audioStream.getAudioTracks()[0]
					this.localStream = openMediaDevices(this.constraintsStart)
					// console.log('Got MediaStream:', stream)

				} catch (error) {
					this.localStream = null
					this.isLocalVideoPlaying = false
					console.error('Error accessing media devices.', error)
				}
			}
		},

		// Configure the Transcoder to record the video using the quality selected
		setTranscoder() {
			this.$q.loading.show()

			let fd = new FormData()
			fd.append('bitrate_video', this.transcoderSettings.bitrate_video)
			fd.append('aspect_ratio_width', this.transcoderSettings.aspect_ratio_width)
			fd.append('aspect_ratio_height', this.transcoderSettings.aspect_ratio_height)
			fd.append('aspect_h264_profile', this.transcoderSettings.aspect_h264_profile)

			this.$axios.post(`/admin_webcasts/webcasts/${this.id_webcast}/config_streaming/`, fd, this.getHeader)
				.then(response => {
                        this.$q.notify({
                            message: this.$t('config_save_ok'),
                            type: 'positive',
                            icon: 'far fa-save'
						})
						this.transcoderConfigured = true
						this.$q.loading.hide()
                    })
                    .catch(e => {
						this.$q.notify({
                            message: this.$t('config_save_error'),
                            type: 'negative',
                            icon: 'fas fa-exclamation-triangle'
						})
						this.transcoderConfigured = false
						this.$q.loading.hide()
                        console.log(e)
                    })
		},

		// Set the constraints for the startup
		async startConstraintsAndTranscoder() {
			let audioSource = this.audioInputDevice
			let videoSource = this.videoInputDevice
			let options = this.recordingChoices[this.recordingChoice - 1]

			this.transcoderSettings.bitrate_video = options.bitRate
			this.transcoderSettings.aspect_ratio_width = options.width
			this.transcoderSettings.aspect_ratio_height = options.height
			this.transcoderSettings.h264_profile = options.profile
			this.videoSettings.videoBitrate = options.bitRate

			if (!videoSource) {
				this.constraintsStart = {
					audio: { echoCancellation: true, noiseSuppresion: true },
					video: { height: options.height, width: options.width }
				}
			} else {
				this.constraintsStart = {
					audio: {
						echoCancellation: true, noiseSuppresion: true,
						deviceId: audioSource ? { exact: audioSource } : undefined
						},
					video: { height: options.height, width: options.width }
				}
				this.constraintsCamera = this.constraintsStart
			}
			this.configureStreamingModal = false
			this.setTranscoder()
			return await this.constraintsStart
		},

		// Capture microphone audio stream as soon as it starts
		async startAudioCapture() {

			const openMediaDevices = async constraints => {
					this.audioStream = await navigator.mediaDevices.getUserMedia(constraints)
					this.isMicrophoneEnabled = true
					return this.audioStream
				}
				try {
					this.audioStream = openMediaDevices({ video: false, audio: this.constraintsStart.audio})

				} catch (error) {
					this.isMicrophoneEnabled = false
					console.error('Error accessing media devices.', error)
				}
		},

		// Get audio input devices (microphone)
		async getAudioInputDevices() {
			const audioInputs = await this.getConnectedDevices('audioinput')
			let audioDevices = audioInputs.map(device => device)

			if(!audioDevices.length) {
				this.$q.notify({
					message: this.$t('no_audio_input_device_found'),
					icon: 'fas fa-exclamation-triangle'
				})
				this.hasMicrophone = false
			} else {
				let mappedAudioInputs = audioDevices.map(device => {
					if (device) {
						if (device.label === '') {
							return {
								...device,
								value: device.deviceId,
								kind: device.kind,
								label: this.$t('default_device')
							}
						} else {
							return {
								...device,
								value: device.deviceId,
								kind: device.kind,
								label: device.label,
							}
						}
					}
				})
				this.hasMicrophone = true
				this.audioInputDevices = mappedAudioInputs
				this.audioInputDevice = this.audioInputDevices[0].value
			}
		},

		// Get video input devices (Cameras -> Webcam, external câmera)
		async getVideoInputDevices() {
			const videoInputs = await this.getConnectedDevices('videoinput')
			let videoDevices = videoInputs.map(device => device)

			if (!videoDevices.length) {
				this.$q.notify({
					message: this.$t('no_video_input_device_found'),
					icon: 'fas fa-exclamation-triangle'
				})
				this.hasCamera = false
			} else {
				// console.table(videoDevices)
				let mappedVideoInputs = videoDevices.map(device => {
					if (device) {
						if (device.label === '') {
							return {
								...device,
								value: device.deviceId,
								kind: device.kind,
								label: this.$t('default_device')
							}
						} else {
							return {
								...device,
								value: device.deviceId,
								kind: device.kind,
								label: device.label
							}
						}
					}
				})
				this.hasCamera = true
				this.videoInputDevices = mappedVideoInputs
				this.videoInputDevice = this.videoInputDevices[0].value
			}
		},

		// if this.browser is good - go check the devices.
		async getConnectedDevices(type) {
			const devices = await navigator.mediaDevices.enumerateDevices()
			return devices.filter(device => device.kind === type)
		},

		// Check if the browser is capable of streaming using WebRTC:
		checkBrowserReady() {
			if(navigator.mediaDevices) {
				this.isBrowserCapable = true
			} else {
				this.$q.notify({
				message: this.$t('app_requirements_not_met'),
				icon: 'far fa-window-maximize',
				color: 'negative'
			})
			}
		}
	},

	async mounted() {
		if (this.id_webcast != 0) this.fetchData()

		this.userAgent = navigator.userAgent.toLowerCase()
		window.RTCPeerConnection = this.vueRTCPC
		window.RTCIceCandidate = this.vueRTCIC
		window.RTCSessionDescription = this.vueRTCSD

		this.streamState()
		this.checkBrowserReady()

		if (this.isBrowserCapable) {
			try {
				await this.getVideoInputDevices()
				await this.getAudioInputDevices()
				await this.startAudioCapture()
			} catch (err) {
				console.error(err)
			}
		}
	}
}
</script>

<style scoped lang="stylus">
.video-container {
	video {
		z-index 1
		margin 10px auto
	}
	.video-buttons {
		z-index 2
		position relative
		top 20px
		.q-btn {
			margin 0 10px
			background rgba(0, 0, 0, 0.6)
		}
	}
}

.frame-wide {
	width 78vw
	max-width 1280px
}

.frame-narrow {
	width 50vw
}

i {
  font-size 3rem
  &:hover {
    cursor pointer
  }
}

.active {
  color warning
}
.inactive {
  color black
}

</style>
